/**
 * React Query Client Configuration
 * Global query client with default options
 */

import { type QueryClient, QueryCache, MutationCache, onlineManager } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useToastStore, type ToastAction } from '@/shared/lib/toast';
import { getApiErrorMessage } from '../lib/getApiErrorMessage';
import { Sentry } from '../lib/sentry';
import { useAuthStore } from '../model/authStore';
import { shouldCaptureApiError } from './sentryCapture';
import { isRequestCanceledError } from './requestCancel';
import { isWebAuthnCancelError } from './webauthnCancel';
import { NetworkQueryClient } from './networkQueryClient';
import { bindMutationReportOwner } from './networkReportScope';
import {
  attachNetworkReporting,
  cancelPendingNetworkReport,
  captureFinalNetworkError,
  discardNetworkReports,
} from './networkReporting';

/**
 * Default stale time for queries (5 minutes)
 */
const DEFAULT_STALE_TIME = 5 * 60 * 1000;

/**
 * Default retry count
 */
const DEFAULT_RETRY_COUNT = 3;

/**
 * Check if error is retryable
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isRequestCanceledError(error)) return false;
  if (failureCount >= DEFAULT_RETRY_COUNT) {
    return false;
  }

  // Handle AxiosError - retry on network errors and server errors
  if (error instanceof AxiosError) {
    if (!error.response) {
      return true; // Network error - retry
    }
    const status = error.response.status;
    // Don't retry client errors (4xx)
    if (status >= 400 && status < 500) {
      return false;
    }
    // Retry on server errors (5xx)
    return status >= 500 && status < 600;
  }

  return false;
}

/**
 * SYS-1: query(read) 실패를 사용자에게 알리는 토스트.
 * 현재 떠 있는 동일 메시지 토스트가 있으면 생략 — 한 화면의 여러 쿼리가 동시에
 * 실패해도(예: 5xx) 토스트가 폭주하지 않게. 토스트는 자동 소멸하므로 모듈 상태 없이 자연 디바운스.
 */
function showQueryErrorToast(message: string, action: ToastAction): void {
  const store = useToastStore.getState();
  if (store.toasts.some((t) => t.title === message)) return;
  store.addToast({ type: 'error', title: message, action });
}

/**
 * Create and configure QueryClient
 */
export function createQueryClient(): QueryClient {
  // 토스트의 "다시 시도"는 실패한 그 쿼리 하나가 아니라 **실패 상태인 활성 쿼리 전부**를 다시 부른다.
  // 같은 문구 토스트는 하나로 접히므로(위 dedupe) 토스트 하나가 한 화면의 실패 여럿을 대표한다 —
  // 첫 쿼리만 부르면 나머지는 빈 채 남는다. 화면을 떠난 쿼리(inactive)는 돌아올 때 스스로 다시 뜬다.
  const retryFailedActiveQueries = (): void => {
    void client.refetchQueries({ type: 'active', predicate: (q) => q.state.status === 'error' });
  };
  const client: QueryClient = new NetworkQueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        cancelPendingNetworkReport(error);
        if (isRequestCanceledError(error)) {
          discardNetworkReports(error, query);
          return;
        }
        // hover 워밍(prefetchByView)은 사용자가 요청한 적 없는 백그라운드 작업이라 실패가
        // 사용자에게도 Sentry 에도 보일 이유가 없다. 특히 stale 배포 상황에서는 DeploymentBanner 가
        // vite:preloadError 를 preventDefault() 하는 탓에 Vite preload 헬퍼가 던지지 않고
        // undefined 로 resolve 하고(dist/node/chunks/config.js `baseModule().catch(handlePreloadError)`),
        // queryFn 의 `(m) => m.xxxApi...` 가 undefined 를 역참조해 TypeError 가 된다.
        // 그건 앱 버그가 아니라 "새로고침하세요" 배너가 이미 담당하는 상태다.
        // 같은 엔드포인트를 사용자가 실제로 열면 그 쿼리는 meta 없이 돌아 정상 보고된다.
        if (query.meta?.prefetch) {
          discardNetworkReports(error, query);
          return;
        }

        const status = error instanceof AxiosError ? error.response?.status : undefined;
        // 401은 인터셉터 단독 영역(refresh/만료 cascade) — 토스트·Sentry 모두 스킵.
        if (status === 401) {
          captureFinalNetworkError(error, { query_error: 'true' }, query);
          return;
        }

        // SYS-1: read 실패를 사용자에게 알린다 — 실패가 빈 상태("0건")로 위장되지 않게.
        // TanStack v5는 query 단위 onError가 없어 QueryCache가 유일한 전역 처리처.
        // 컴포넌트가 자체 에러 UI를 가진 쿼리는 meta.skipGlobalErrorToast 로 옵트아웃.
        if (!query.meta?.skipGlobalErrorToast) {
          const message =
            typeof query.meta?.errorMessage === 'string'
              ? query.meta.errorMessage
              : getApiErrorMessage(error, '데이터를 불러오지 못했습니다.');
          showQueryErrorToast(message, {
            label: '다시 시도',
            onClick: retryFailedActiveQueries,
          });
        }

        if (captureFinalNetworkError(error, { query_error: 'true' }, query)) return;
        // axios 인터셉터(client.ts)가 캡처할 HTTP 에러는 Sentry 스킵 — 같은 에러를 두 번
        // 보고하면 이벤트 볼륨이 5xx retry 횟수만큼 증폭된다.
        if (error instanceof AxiosError && shouldCaptureApiError(error)) return;
        // 「없음」을 4xx 로 답하는 조회는 호출부가 meta.expectedStatuses 로 알린다 — 앱 버그가 아니다.
        const expected = query.meta?.expectedStatuses;
        if (status !== undefined && Array.isArray(expected) && expected.includes(status)) return;
        Sentry.captureException(error, {
          tags: {
            query_error: true,
            status_code: status ? String(status) : 'unknown',
          },
        });
      },
    }),
    mutationCache: new MutationCache({
      onMutate: (_variables, mutation, context) => bindMutationReportOwner(context, mutation),
      onError: (error, _variables, _onMutateResult, mutation) => {
        cancelPendingNetworkReport(error);
        // WebAuthn 사용자 취소(패스키 "다른 기기로 등록" QR창 닫기·프롬프트 거부·타임아웃)는
        // 앱 버그가 아니라 사용자/환경 사유다. 패스키 훅이 NotAllowedError 를 한국어 메시지로
        // 감싼 non-Axios Error 라 아래 Axios 가드를 통과 못 하고 captureException 으로 직행하므로
        // 여기서 먼저 거른다. (QueryCache 는 WebAuthn 을 발생시키지 않아 미적용)
        if (isWebAuthnCancelError(error)) {
          discardNetworkReports(error, mutation);
          return;
        }
        // 이탈·재제출로 끊은 요청은 실패가 아니라 사용자 의도다. shouldCaptureApiError 가
        // 취소를 false 로 돌리면서 아래 "인터셉터가 이미 캡처했으니 skip" early-return 이
        // 취소에 대해 풀렸다 — 여기서 막지 않으면 이벤트가 사라지는 게 아니라
        // mutation_error 태그로 재라벨돼 그대로 나간다.
        if (isRequestCanceledError(error)) {
          discardNetworkReports(error, mutation);
          return;
        }
        if (captureFinalNetworkError(error, { mutation_error: 'true' }, mutation)) return;
        if (error instanceof AxiosError) {
          if (shouldCaptureApiError(error)) return;
          if (error.response?.status === 401) return;
        }
        const status = error instanceof AxiosError ? error.response?.status : undefined;
        Sentry.captureException(error, {
          tags: {
            mutation_error: true,
            status_code: status ? String(status) : 'unknown',
          },
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        retry: shouldRetry,
        refetchOnWindowFocus: false,
        gcTime: 10 * 60 * 1000, // 10 minutes garbage collection time
      },
      mutations: {
        retry: false, // Don't retry mutations by default
        onError(error) {
          // 이탈·재제출로 진행 중이던 요청을 끊은 건 실패가 아니다. axios 는 'canceled',
          // fetch 는 'AbortError' 라는 영문 원문을 message 로 갖고 있어 그대로 토스트되면
          // 취소한 화면을 떠난 뒤에 영어 에러가 뜬다(ToastContainer 는 앱 루트에 상주).
          if (isRequestCanceledError(error)) return;
          // WebAuthn 프롬프트를 사용자가 닫거나 거부한 것도 실패가 아니다. 이 판별이
          // 여기 있어야 패스키 훅들이 자기 onError 로 전역 토스트를 대체하지 않는다 —
          // 훅에 onError 를 두면 얕은 병합이 이 핸들러를 통째로 지워 429 를 포함한
          // 모든 패스키 실패가 무음이 된다.
          if (isWebAuthnCancelError(error)) return;
          // 409 Conflict는 비즈니스 로직 응답 (중복 등) — 호출부에서 개별 처리
          if (error instanceof AxiosError && error.response?.status === 409) {
            return;
          }
          const message = getApiErrorMessage(error, '요청 처리 중 오류가 발생했습니다.');
          useToastStore.getState().addToast({ type: 'error', title: message });
        },
      },
    },
  });
  attachNetworkReporting(client);
  return client;
}

/**
 * Online/offline detection — pauses queries when offline, refetches on reconnect
 */
if (typeof window !== 'undefined') {
  onlineManager.setEventListener((setOnline) => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    setOnline(navigator.onLine);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  });
}

/**
 * Default query client instance
 */
export const queryClient = createQueryClient();

// AXIOM-WEB-2 Tier 4-B: auth user 가 non-null → null (또는 다른 user) 로 transition
// 될 때 cache 를 즉시 비운다. logout / clearAuth / cross-tab logout 모두 해당.
// stale window 의 background refetch 가 logout 직후에도 fire 되어 401 cascade 가
// 재발화하는 회귀 차단.
//
// top-level 부수효과로 등록하면 sentry → router → routeTree → root → Providers
// → QueryProvider → queryClient → authStore → sentry 순환 import 가 useAuthStore
// hoist 전에 .subscribe 를 건드려 TypeError 가 난다. 명시적 init 단계로 분리.
let authCacheResetAttached = false;
export function attachAuthCacheReset(): () => void {
  if (authCacheResetAttached) return () => undefined;
  authCacheResetAttached = true;
  const unsubscribe = useAuthStore.subscribe((state, prev) => {
    const prevId = prev.user?.id ?? null;
    const nextId = state.user?.id ?? null;
    if (prevId !== null && prevId !== nextId) {
      queryClient.clear();
    }
  });
  return () => {
    unsubscribe();
    authCacheResetAttached = false;
  };
}
